import styled from "styled-components";
import { Zap, Banknote, X, AlertTriangle } from "lucide-react";

import { theme } from "../theme";
import { useT } from "../i18n";

// The footer of the payment dialog: what this selection costs, and the two ways to settle it.
//
// Every figure here comes from the server (folio_quote). The client cannot compute it: the online
// deposit fee applies only when the selection routes to the deposit carrier in link mode, and cash
// permission reads a DIFFERENT setting depending on which carrier the selection lands on — so the
// applicable rule changes underneath the FO as they tick rows. Guessing it client-side would mean a
// total that changes after the front desk has said it out loud.
const Wrap = styled.div`
  background: #fbfaf7;
  border-radius: 0 0 14px 14px;
  border-top: 1px solid ${theme.border};
  padding: 0.8rem 1.1rem;
`;

const Row = styled.div`
  align-items: baseline;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.2rem;
`;

const Label = styled.span`
  color: #4a4f63;
  font-size: 0.82rem;
  font-weight: 700;
`;

const Value = styled.span`
  color: ${theme.ink};
  font-size: 0.86rem;
  font-weight: 800;
`;

const TotalRow = styled(Row)`
  border-top: 1px solid ${theme.border};
  margin-top: 0.45rem;
  padding-top: 0.45rem;
`;

const TotalValue = styled.span`
  color: ${theme.ink};
  font-size: 1.2rem;
  font-weight: 800;
`;

// The deposit is money held in trust, not revenue. It gets its own subtotal that never merges into
// the charge total, and its own green treatment, on every screen that shows it.
const HeldRow = styled(Row)`
  color: #1b6e56;
`;

const Blocked = styled.div`
  align-items: flex-start;
  background: #fdecea;
  border: 1px solid #f7cfca;
  border-radius: 8px;
  color: #b42318;
  display: flex;
  font-size: 0.8rem;
  font-weight: 700;
  gap: 0.35rem;
  margin-bottom: 0.55rem;
  padding: 0.5rem 0.6rem;
`;

const Note = styled.div`
  color: #4a4f63;
  font-size: 0.76rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  justify-content: flex-end;
  margin-top: 0.6rem;
`;

const Btn = styled.button`
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 800;
  gap: 0.32rem;
  padding: 0.52rem 0.95rem;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Ghost = styled(Btn)`
  background: #ffffff;
  border: 1px solid ${theme.border};
  color: ${theme.ink700};
`;

const Cash = styled(Btn)`
  background: ${theme.ink};
  border: 0;
  color: #ffffff;
`;

const Link = styled(Btn)`
  background: #4373fc;
  border: 0;
  color: #ffffff;
`;

const QuoteFooter = (props) => {
  const { t } = useT();
  const quote = props.quote || {};
  const fmt = (value) => `Rp${Number(value || 0).toLocaleString("en-US")}`;
  const blocked = Boolean(quote.blocked_reason);
  const nothing = !props.hasSelection;
  /* A down payment charges a share of the stay, so the subtotal it prices is NOT the bill. Saying so
   * on the row itself, and naming what is left, is the difference between a figure the FO can read
   * out to the guest and one that looks like the price changed by itself.
   */
  const dpPercent = Number(quote.down_payment_percent || 0);
  const dpResidual = Number(quote.down_payment_residual || 0);

  return (
    <Wrap>
      {blocked ? (
        <Blocked>
          <AlertTriangle size={14} />
          {quote.blocked_reason}
        </Blocked>
      ) : null}

      <Row>
        <Label>
          {dpPercent > 0
            ? t("pay_down_payment_row").replace("%{percent}", dpPercent)
            : t("pay_subtotal")}
        </Label>
        <Value>{fmt(quote.subtotal)}</Value>
      </Row>
      {dpResidual > 0 ? (
        <Row>
          <Label>{t("pay_down_payment_residual")}</Label>
          <Value style={{ color: "#8a5a00" }}>{fmt(dpResidual)}</Value>
        </Row>
      ) : null}
      {Number(quote.discount || 0) > 0 ? (
        <Row>
          <Label>{t("pay_discount")}</Label>
          <Value style={{ color: "#1b6e56" }}>-{fmt(quote.discount)}</Value>
        </Row>
      ) : null}
      {/* Walk-in room tax (RoomTax): only on a taxed booking's room charge, so an untaxed quote reads
       * exactly as before.
       */}
      {Number(quote.tax || 0) > 0 ? (
        <Row>
          <Label>{t("pay_room_tax")}</Label>
          <Value>{fmt(quote.tax)}</Value>
        </Row>
      ) : null}
      {Number(quote.online_fee || 0) > 0 ? (
        <Row>
          <Label>{t("pay_online_fee")}</Label>
          <Value>{fmt(quote.online_fee)}</Value>
        </Row>
      ) : null}
      {Number(props.heldTotal || 0) > 0 ? (
        <HeldRow>
          <Label style={{ color: "#1b6e56" }}>{t("pay_held")}</Label>
          <Value style={{ color: "#1b6e56" }}>{fmt(props.heldTotal)}</Value>
        </HeldRow>
      ) : null}
      <TotalRow>
        <Label>{t("pay_total")}</Label>
        <TotalValue>{fmt(quote.total)}</TotalValue>
      </TotalRow>

      {/* The voucher's own reason belongs next to the voucher field in the body, not here — printing
          it twice made one refusal look like two. */}
      {quote.cash_reason ? <Note>{quote.cash_reason}</Note> : null}

      <Actions>
        <Ghost type="button" onClick={props.onCancel}>
          <X size={14} /> {t("confirm_cancel")}
        </Ghost>
        {quote.cash_allowed ? (
          <Cash
            type="button"
            disabled={nothing || blocked || props.busy || props.loading}
            onClick={() => props.onSettle("cash")}
          >
            <Banknote size={14} /> {t("pay_cash")}
          </Cash>
        ) : null}
        <Link
          type="button"
          disabled={nothing || blocked || props.busy || props.loading}
          onClick={() => props.onSettle("link")}
        >
          <Zap size={14} /> {t("pay_link")}
        </Link>
      </Actions>
    </Wrap>
  );
};

export default QuoteFooter;
